/* eslint-disable react/no-unused-prop-types,no-console */
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'react-moment';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Done from '@material-ui/icons/Done';
import Edit from '@material-ui/icons/Edit';
import Check from '@material-ui/icons/Check';
import Updated from '@material-ui/icons/EditOutlined';
import Visibility from '@material-ui/icons/Visibility';
import Pause from '@material-ui/icons/Pause';
import Play from '@material-ui/icons/PlayArrow';
import Close from '@material-ui/icons/Clear';
import Schedule from '@material-ui/icons/Schedule';
import Agency from '@material-ui/icons/SupervisorAccountRounded';
import Person from '@material-ui/icons/Person';
import ArrowForward from '@material-ui/icons/ArrowForward';
import LinearProgress from '@material-ui/core/LinearProgress';
import FilterIco from '@material-ui/icons/FilterList';
import Refresh from '@material-ui/icons/Refresh';
// material-ui/ core components
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import Checkbox from '@material-ui/core/Checkbox';
import { Zoom, Tooltip, Hidden } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import Snackbar from 'components/UI/Snackbar/Snackbar';
// global variables
import { adStatus, paidAdStatus } from 'config';
// our components
import { PROMOTED_AD, USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import ModalConfirmation from 'components/common/ModalConfirmation';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import { TableLoader } from 'components/common/TableLoader';
import RedirectFlashMessage from 'components/common/RedirectFlashMessage';
import Filter from 'components/PromotedAd/Filter';
import WithPermission from 'components/User/WithPermission';
import Badge from 'components/UI/Badge/Badge';
import { defaultFilters } from 'components/PromotedAd/List';
import dateFormat from 'helpers/dateHelper';
import {
  getPaidAdDetailsUrl,
  getPaidAdTitle,
  getPaidAdDescription,
} from 'helpers/functions';
import ImageMosaic from 'components/common/ImageMosaic/ImageMosaic';

/**
 * @class
 * @extends React.Component
 */
class PromotedAdList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      modalIsOpen: false,
      itemsToCancel: [],
      showFilters: false,
      currentAction: undefined,
    };
  }

  /**
   * @description
   * Select all items.
   *
   * @param {Array} edges - All ads from api.
   * @returns {undefined} No return.
   */
  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    // if all are selected
    if (selectedItems.length === edges.length) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: edges.map(item => item.node.id) });
    }
  };

  /**
   * @description
   * Select an item.
   *
   * @param {number} id - Id of selected item.
   * @returns {undefined} No return.
   */
  handleSelectItem = id => {
    const { selectedItems } = this.state;
    // if item is selected
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  /**
   * @description
   * Get underlying ad ids for selected promoted ads.
   *
   * @param {Array} selectedItems - Promoted ad ids.
   * @returns {Array} Underlying ad ids.
   */
  getAdIdsFromSelectedItems = selectedItems => {
    const { data } = this.props;
    return selectedItems
      .map(item => {
        const edge = data.promotedAds.edges.find(
          ({ node }) => node.id === item,
        );
        return edge && edge.node.ad && edge.node.ad.id;
      })
      .filter(Boolean);
  };

  /**
   * @description
   * Handle delete, publish, unpublish or review action.
   *
   * @param {string} action - Specific action.
   * @param {Array} selectedItems - Items ids.
   * @returns {undefined} - No returns value.
   */
  handleAction = (action, selectedItems) => {
    const {
      adReview,
      adPublish,
      paidAdExtend,
      paidAdPause,
      paidAdResume,
    } = this.props;
    this.setState({ currentAction: action });
    switch (action) {
      case 'review': {
        const adIds = this.getAdIdsFromSelectedItems(selectedItems);
        if (adIds.length > 0) {
          adReview(adIds);
        }
        break;
      }
      case 'publish': {
        const adIds = this.getAdIdsFromSelectedItems(selectedItems);
        if (adIds.length > 0) {
          adPublish(adIds);
        }
        break;
      }
      case 'pause': {
        paidAdPause(selectedItems);
        break;
      }
      case 'resume': {
        paidAdResume(selectedItems);
        break;
      }
      case 'extend': {
        paidAdExtend(selectedItems);
        break;
      }
      case 'cancel': {
        this.handleOpenModal(selectedItems);
        break;
      }
      default:
        break;
    }
    this.setState({ selectedItems: [] });
  };

  /**
   * @description
   * Redirect to paidad/create route.
   */
  handleCreatePromotedAd = () => {
    const history = this.context;
    history.push(PROMOTED_AD.CREATE);
  };

  /**
   * @description
   * Redirect to ads/details route.
   *
   * @param {string} id - Ad id.
   * @param {string} paidAdType - Type.
   */
  handleDetailsPromotedAd = (id, paidAdType) => {
    const history = this.context;
    const url = getPaidAdDetailsUrl(id, paidAdType);
    history.push(url);
  };

  /**
   * @description
   * Redirect to user/details route.
   *
   * @param {string} id - User id.
   */
  handleDetailsUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  /**
   * @description
   * Redirect to paidads/details route.
   *
   * @param {string} id - PromotedAd id.
   */
  handlePromotedAdUpdate = id => {
    const history = this.context;
    history.push(formatRoute(PROMOTED_AD.UPDATE, { id }));
  };

  /**
   * @description
   * Open destroying modal.
   *
   * @param {Array} itemsToCancel - Ids of the selected items.
   */
  handleOpenModal = itemsToCancel => {
    this.setState({
      modalIsOpen: true,
      itemsToCancel,
    });
  };

  /**
   * @description
   * Callback to close destroying modal if user close it manually.
   */
  handleCloseModal = () => {
    this.setState({
      modalIsOpen: false,
      currentAction: '',
      itemsToCancel: [],
    });
  };

  /**
   * @description
   * Confirm delete callback.
   * @returns {undefined} No return.
   *
   */
  handleSubmit = () => {
    const { itemsToCancel, selectedItems, currentAction } = this.state;
    const { paidAdCancel, paidAdExtend } = this.props;

    if (currentAction === 'extend') {
      paidAdExtend(selectedItems);
    } else {
      paidAdCancel(itemsToCancel);
    }

    this.setState({
      itemsToCancel: [],
      selectedItems: [],
      modalIsOpen: false,
    });
  };

  /**
   * @description
   * Handle show collapsed or full filters.
   */
  handleShowFilters = () => {
    const { showFilters } = this.state;
    this.setState({ showFilters: !showFilters });
  };

  /**
   * @description
   * Render Status cell.
   *
   * @param {Object} node - An ad.
   * @returns {*} Status cell.
   */
  renderCellStatus = node => {
    const { classes, t } = this.props;

    switch (node.status) {
      case paidAdStatus.PENDING:
        return (
          <div>
            <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
              {t(`promoted_ad.status.${paidAdStatus.PENDING}`)}
            </h6>
            {node.ad &&
              (node.ad.status !== adStatus.PUBLISHED_BY_MODERATOR &&
              node.ad.status !== adStatus.PUBLISHED_BY_PROCESSOR ? (
                <p className={`${classes.cellStatus}`}>
                  {t('promoted_ad.list.paidad_status.pending_publish')}
                </p>
              ) : (
                !node.ad.reviewed && (
                  <p className={`${classes.cellStatus}`}>
                    {t('promoted_ad.list.paidad_status.pending_review')}
                  </p>
                )
              ))}
          </div>
        );
      case paidAdStatus.PAUSED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.PAUSED}`)}
          </h6>
        );
      case paidAdStatus.COMPLETED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.completedTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.COMPLETED}`)}
          </h6>
        );
      case paidAdStatus.RUNNING:
        return (
          <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.RUNNING}`)}
          </h6>
        );
      case paidAdStatus.SCHEDULED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.scheduledTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.SCHEDULED}`)}
          </h6>
        );
      case paidAdStatus.CANCELLED:
        return (
          <h6 className={`${classes.cellStatus} ${classes.deletedTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.CANCELLED}`)}
          </h6>
        );
      case paidAdStatus.NO_CREDIT:
        return (
          <h6 className={`${classes.cellStatus} ${classes.pendingTag}`}>
            {t(`promoted_ad.status.${paidAdStatus.NO_CREDIT}`)}
          </h6>
        );
      default:
        break;
    }
    return null;
  };

  /**
   * @description
   * Render Review button.
   *
   * @param {Object} node - Ad from api.
   * @returns {*} Review button.
   */
  renderCellReview = node => {
    const { classes, t, adReview, adPublish } = this.props;
    const canShowModerationActions =
      node.status === paidAdStatus.PENDING ||
      node.status === paidAdStatus.RUNNING;

    if (!canShowModerationActions) {
      return (
        <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
          {t('promoted_ad.list.paidad_status.reviewed')}
        </h6>
      );
    }

    if (!node.ad) {
      return (
        <h6 className={`${classes.cellStatus} ${classes.unknownTag}`}>
          {t('promoted_ad.list.paidad_status.missing_ad')}
        </h6>
      );
    }

    if (node.ad.isPublished && node.ad.reviewed) {
      return (
        <h6 className={`${classes.cellStatus} ${classes.runningTag}`}>
          {t('promoted_ad.list.paidad_status.reviewed')}
        </h6>
      );
    }

    return (
      <div className={classes.cellStatus}>
        {node.ad.isPublished && !node.ad.reviewed && (
          <WithPermission
            alt={
              <div className={classes.reviewCheck} data-cy="adReviewedSpan">
                <strong>{t('ad.list.not_reviewed')}</strong>
              </div>
            }
            allowPermissions={['review_ad']}
          >
            <Button
              data-cy="adReviewButton"
              color="success"
              id={`ads-review-${node.ad.id}`}
              onClick={() => adReview([node.ad.id])}
            >
              <span>{t('common.review')}</span>
            </Button>
          </WithPermission>
        )}
        {!node.ad.isPublished && (
          <WithPermission
            alt={
              <div className={classes.reviewCheck}>
                <span>
                  <b data-cy="adReviewedSpan">{t('ad.list.not_published')}</b>
                </span>
              </div>
            }
            allowPermissions={['publish_ad']}
          >
            <Button
              data-cy="adPublishButton"
              color="primary"
              id={`ads-publish-${node.ad.id}`}
              onClick={() => adPublish([node.ad.id])}
            >
              <span>{t('common.publish')}</span>
            </Button>
          </WithPermission>
        )}
      </div>
    );
  };

  /**
   * @description
   * To give a row color depending of column status.
   *
   * @param {number} nodeId - Id of an Ad.
   * @returns {string} Row color.
   */
  renderRowColor = nodeId => {
    const { data } = this.props;
    const node = data.promotedAds.edges.find(
      ({ node: { id } }) => id === nodeId,
    );
    switch (node.node.status) {
      case paidAdStatus.PENDING:
        return 'rgba(255, 181, 0, .1)';
      case paidAdStatus.CANCELLED:
        return 'rgba(175, 34, 58, .1)';
      default:
        return '';
    }
  };

  /**
   * @description
   * Description of ReactTable columns.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table columns.
   */
  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={edges && selectedItems.length === edges.length}
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.ad')}
          </span>
        ),
        accessor: 'ad',
        sortable: false,
        filterable: false,
        minWidth: 300,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('paidad.list.headers.paidad')}
          </span>
        ),
        accessor: 'promoted',
        sortable: false,
        filterable: false,
        minWidth: 200,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.start_date')}
          </span>
        ),
        accessor: 'start_date',
        sortable: false,
        filterable: false,
        maxWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.end_date')}
          </span>
        ),
        accessor: 'end_date',
        sortable: false,
        filterable: false,
        maxWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.sales')}
          </span>
        ),
        accessor: 'sales',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.commission')}
          </span>
        ),
        accessor: 'commission',
        sortable: false,
        filterable: false,
        maxWidth: 90,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('promoted_ad.list.headers.status')}</p>
          </span>
        ),
        accessor: 'status',
        sortable: false,
        filterable: false,
        maxWidth: 85,
      },
      {
        Header: (
          <span className={classes.headerField} style={{ textAlign: 'center' }}>
            <p>{t('promoted_ad.list.headers.moderation')}</p>
          </span>
        ),
        accessor: 'moderation',
        sortable: false,
        filterable: false,
        minWidth: 100,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('promoted_ad.list.headers.actions')}
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 125,
        maxWidth: 300,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  /**
   * @description
   * Format the DATE received from the server.
   *
   * @param {string} date - DATE received from the server.
   * @returns {Object} Converted date.
   */
  dateFormat = date => {
    const calendarStrings = {
      lastDay: '[Ayer ] LT',
      sameDay: '[Hoy ] LT',
      lastWeek: '[] dddd[, ] LT',
      sameElse: 'L',
    };
    // 2019-02-08T15:59-0500 date for testing
    return <Moment date={date} calendar={calendarStrings} />;
  };

  /**
   * @description
   * Map the data from api to ReactTable rows.
   *
   * @param {Array} edges - All ads from api.
   * @returns {Array} Table rows.
   */
  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return edges.map(({ node }) => ({
      id: node.id,
      checkbox: (
        <Checkbox
          onClick={() => {
            this.handleSelectItem(node.id);
          }}
          checked={selectedItems.indexOf(node.id) !== -1}
          checkedIcon={<Check className={classes.checkedIcon} />}
          icon={<Check className={classes.uncheckedIcon} />}
          classes={{
            checked: classes.checked,
          }}
        />
      ),
      ad: (
        <React.Fragment>
          <WithPermission
            alt={
              node.ad ? (
                <div
                  id={`ad-title-${node.ad.id}`}
                  data-cy="adTitle"
                  className={classes.adTitleContainer}
                >
                  {node.ad.readyImages && (
                    <ImageMosaic
                      images={node.ad.readyImages.map(({ id, urls }) => ({
                        id,
                        ...urls,
                      }))}
                    />
                  )}
                  <div>
                    <ul
                      className={classes.adCatBread}
                      style={{ color: '#217db2' }}
                    >
                      <li
                        data-cy="adCategory"
                        style={{ padding: '0', margin: 0 }}
                      >
                        {node.ad.subcategory.parentCategory &&
                          node.ad.subcategory.parentCategory.title}
                      </li>
                      <li className={classes.adIconBread}>
                        <ArrowForward color="disabled" />
                      </li>
                      <li
                        data-cy="adSubcategory"
                        style={{ padding: '0', margin: 0 }}
                      >
                        {node.ad.subcategory && node.ad.subcategory.title}
                      </li>
                    </ul>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <div className={classes.idAndDate}>
                        <div className={classes.searchFieldContainer}>
                          <b data-cy={node.ad.id}>ID: {node.ad.id}</b>
                          <span className={classes.modificationDate}>
                            <Updated /> {node.ad.updateCount} •
                            <span style={{ marginLeft: 4 }}>
                              {node.ad.updatedOnToOrder &&
                                dateFormat(node.ad.updatedOnToOrder, t)}
                            </span>
                          </span>
                          {node.ad.flagCount > 0 && (
                            <span style={{ marginLeft: 8 }}>
                              <Badge color="danger">{node.ad.flagCount}</Badge>
                            </span>
                          )}
                        </div>
                      </div>
                      <p className={classes.adTitle} style={{ fontSize: 12 }}>
                        {node.ad.title}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <h6 className={classes.scheduledTag}>
                  {t('promoted_ad.list.expired_ad')}
                </h6>
              )
            }
            allowPermissions={['view_promotedad', 'view_ownership_promotedad']}
          >
            {node.ad ? (
              <>
                {node.ad.readyImages && (
                  <ImageMosaic
                    images={node.ad.readyImages.map(({ id, urls }) => ({
                      id,
                      ...urls,
                    }))}
                  />
                )}
                <a
                  className={['read', classes.adBtns, classes.titleBtn].join(
                    ' ',
                  )}
                  href={getPaidAdDetailsUrl(node.id, node.paidAdType)}
                  onClick={e => {
                    e.preventDefault();
                    this.handleDetailsPromotedAd(node.id, node.paidAdType);
                  }}
                >
                  <span
                    id={`ad-title-${node.ad.id}`}
                    data-cy="adTitle"
                    className={classes.adTitleContainer}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <div className={classes.idAndDate}>
                        <b data-cy={node.ad.id}>
                          {getPaidAdTitle(node.id, node.paidAdType, t)}
                        </b>
                        {node.flagCount > 0 && (
                          <span style={{ marginLeft: 8 }}>
                            <Badge color="danger">{node.flagCount}</Badge>
                          </span>
                        )}
                        <p className={classes.modificationDate}>
                          <Updated /> {node.ad.updateCount} •
                          <span style={{ marginLeft: 4 }}>
                            {node.ad.updatedOnToOrder &&
                              dateFormat(node.ad.updatedOnToOrder, t)}
                          </span>
                        </p>
                      </div>
                      <ul
                        className={classes.adCatBread}
                        style={{ color: '#217db2' }}
                      >
                        <li
                          data-cy="adCategory"
                          style={{ padding: '0', margin: 0 }}
                        >
                          {node.ad.subcategory.parentCategory &&
                            node.ad.subcategory.parentCategory.title}
                        </li>
                        <li className={classes.adIconBread}>
                          <ArrowForward color="disabled" />
                        </li>
                        <li
                          data-cy="adSubcategory"
                          style={{ padding: '0', margin: 0 }}
                        >
                          {node.ad.subcategory && node.ad.subcategory.title}
                        </li>
                      </ul>

                      <b data-cy={node.ad.id}>ID: {node.ad.id} </b>
                      {node.ad.title}
                    </div>
                  </span>
                </a>
              </>
            ) : (
              <a
                className={['read', classes.adBtns, classes.titleBtn].join(' ')}
                href={getPaidAdDetailsUrl(node.id, node.paidAdType)}
                onClick={e => {
                  e.preventDefault();
                  this.handleDetailsPromotedAd(node.id, node.paidAdType);
                }}
              >
                <div className={classes.idAndDate}>
                  <b data-cy={node.id}>{`${t(
                    'promoted_ad.list.headers.promoted',
                  )}: ${node.id}`}</b>
                  {node.flagCount > 0 && (
                    <span style={{ marginLeft: 8 }}>
                      <Badge color="danger">{node.flagCount}</Badge>
                    </span>
                  )}
                </div>
                <h6 className={classes.scheduledTag}>
                  {t('promoted_ad.list.expired_ad')}
                </h6>
              </a>
            )}
          </WithPermission>
        </React.Fragment>
      ),
      promoted: (
        <div>
          <span className={classes.paidAdFrequencies}>
            {getPaidAdDescription(node, t)}
          </span>
          <WithPermission
            alt={
              node.owner &&
              node.owner.fullname && (
                <div>
                  <Agency />
                  {node.owner.fullname}
                </div>
              )
            }
            allowPermissions={['view_useragency', 'view_ownership_useragency']}
          >
            {node.owner &&
              (node.owner.fullname ? (
                <div>
                  <Button
                    data-cy="paidAdOwner"
                    simple
                    color="primary"
                    className={classes.adBtns}
                    style={{ margin: 0 }}
                    onClick={() => this.handleDetailsUser(node.owner.id)}
                  >
                    <Agency />
                    {node.owner.fullname}
                  </Button>
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 15,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Agency />
                  <span>{`${t('promoted_ad.form.fields.private_user')} (${
                    node.owner.fakeId
                  })`}</span>
                </div>
              ))}
          </WithPermission>
          {node.manager && (
            <WithPermission allowPermissions={['view_agency']}>
              <div>
                <Button
                  data-cy="paidAdManager"
                  simple
                  color="primary"
                  className={classes.adBtns}
                  style={{ margin: 0 }}
                  onClick={() => this.handleDetailsUser(node.manager.id)}
                >
                  <Person />
                  {node.manager && node.manager.fullname}
                </Button>
              </div>
            </WithPermission>
          )}
        </div>
      ),
      start_date: (
        <div data-cy="paidAdStartDate" className={classes.adUserContainer}>
          <p data-cy="paidAdStartDate" key={`paidad-start-date-${node.id}`}>
            <Moment date={node.startDate} format="DD/MM/YYYY" />
          </p>
        </div>
      ),
      end_date: (
        <div data-cy="paidAdEndDate" className={classes.adUserContainer}>
          <p data-cy="paidAdEndDate" key={`paidad-end-date-${node.id}`}>
            <Moment date={node.endDate} format="DD/MM/YYYY" />
          </p>
        </div>
      ),
      sales: (
        <div>
          <p data-cy="agencySales" key={`agency-sales-${node.id}`}>
            {node.secondaryCurrency
              ? `${node.secondaryUserPrice} ${node.secondaryCurrency}`
              : `${node.userPrice} ${node.currency}`}
          </p>
        </div>
      ),
      commission: (
        <div>
          <p data-cy="agencyCommission" key={`agency-commission-${node.id}`}>
            {node.secondaryCurrency
              ? `${node.secondaryCommission} ${node.secondaryCurrency}`
              : `${node.commission} ${node.currency}`}
          </p>
        </div>
      ),
      status: <span data-cy="status">{this.renderCellStatus(node)}</span>,
      moderation: this.renderCellReview(node),
      actions: selectedItems.indexOf(node.id) === -1 &&
        node.status !== paidAdStatus.CANCELLED && (
          // we've added some custom button actions
          <div className="actions-right">
            {/* use this button to update the data row */}
            <WithPermission
              allowPermissions={[
                'pause_resume_promotedad',
                'pause_resume_ownership_promotedad',
              ]}
            >
              {node.status !== paidAdStatus.COMPLETED &&
                ([paidAdStatus.PAUSED, paidAdStatus.NO_CREDIT].includes(
                  node.status,
                ) ? (
                  <Tooltip
                    placement="top"
                    TransitionComponent={Zoom}
                    title={
                      <React.Fragment>
                        {t('common.resume')}
                        <span className={classes.arrow} />
                      </React.Fragment>
                    }
                    classes={{
                      tooltip: classes.bootstrapTooltip,
                      tooltipPlacementTop: classes.bootstrapPlacementTop,
                    }}
                  >
                    <Button
                      data-cy="paidAdResume"
                      justIcon
                      simple
                      onClick={() => this.handleAction('resume', [node.id])}
                      color="primary"
                      className="resume"
                      id={`resume-paidad-${node.id}`}
                    >
                      <Play />
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip
                    placement="top"
                    TransitionComponent={Zoom}
                    title={
                      <React.Fragment>
                        {t('common.pause')}
                        <span className={classes.arrow} />
                      </React.Fragment>
                    }
                    classes={{
                      tooltip: classes.bootstrapTooltip,
                      tooltipPlacementTop: classes.bootstrapPlacementTop,
                    }}
                  >
                    <Button
                      data-cy="paidAdPause"
                      justIcon
                      simple
                      onClick={() => this.handleAction('pause', [node.id])}
                      color="primary"
                      className="pause"
                      id={`pause-paidad-${node.id}`}
                    >
                      <Pause />
                    </Button>
                  </Tooltip>
                ))}
            </WithPermission>
            <WithPermission
              allowPermissions={[
                'extend_ownership_promotedad',
                'extend_promotedad',
              ]}
            >
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('common.extend')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="paidAdExtend"
                  justIcon
                  simple
                  onClick={() => this.handleAction('extend', [node.id])}
                  color="primary"
                  className="extend"
                  id={`extend-paidad-${node.id}`}
                >
                  <Schedule />
                </Button>
              </Tooltip>
            </WithPermission>
            <WithPermission
              allowPermissions={[
                'change_promotedad',
                'change_ownership_promotedad',
              ]}
            >
              <WithPermission allowPermissions={['list_promotedadpackage']}>
                <WithPermission
                  allowPermissions={[
                    'view_promotedad',
                    'view_ownership_promotedad',
                  ]}
                >
                  <Tooltip
                    placement="top"
                    TransitionComponent={Zoom}
                    title={
                      <React.Fragment>
                        {t('common.edit')}
                        <span className={classes.arrow} />
                      </React.Fragment>
                    }
                    classes={{
                      tooltip: classes.bootstrapTooltip,
                      tooltipPlacementTop: classes.bootstrapPlacementTop,
                    }}
                  >
                    <Button
                      data-cy="paidAdUpdate"
                      justIcon
                      simple
                      onClick={() => {
                        this.handlePromotedAdUpdate(node.id);
                      }}
                      color="primary"
                      className="edit"
                      id={`edit-paidad-${node.id}`}
                    >
                      <Edit />
                    </Button>
                  </Tooltip>
                </WithPermission>
              </WithPermission>
            </WithPermission>
          </div>
        ),
    }));
  };

  /**
   * @description
   * Map Loading index Rows with itemProcessing.
   *
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @param {Object} edges - Edges of connection data.
   */
  mapLoadingRows = (itemProcessing, edges) => {
    const { currentAction } = this.state;
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (currentAction === 'review' || currentAction === 'publish') {
        if (value.node.ad && itemProcessing.includes(value.node.ad.id)) {
          loadingRows[index] = true;
        }
      } else if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  /**
   * @description
   * Render Filter, Paginator and ReactTable components.
   * @returns {*} Filter, Paginator and ReactTable components.
   */
  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      refresh,
      t,
      failedIds,
      notification,
      closeNotification,
    } = this.props;
    const {
      modalIsOpen,
      selectedItems,
      itemsToCancel,
      showFilters,
    } = this.state;
    const edges = loading || data === undefined ? [] : data.promotedAds.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    return (
      <React.Fragment>
        <RedirectFlashMessage />
        <Snackbar
          place="tc"
          color={notification.type === 'success' ? 'success' : 'danger'}
          message={Object.keys(notification).length > 0 && notification.message}
          open={Object.keys(notification).length > 0}
          closeNotification={closeNotification}
          close
        />

        <ModalConfirmation
          type="danger"
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseModal()}
          title={
            <span>
              {itemsToCancel.length > 1
                ? t('common.cancel_paidad.plural')
                : t('common.cancel_paidad.paidad')}
            </span>
          }
          description={itemsToCancel.length === 1 && itemsToCancel[0]}
          confirmLabel={`${t('common.cancel_paidad.confirm')} ${
            selectedItems.length > 1 ? 's' : ''
          }`}
        />

        <GridContainer>
          <GridItem xs={12}>
            <div className={classes.pageContainer}>
              <GridContainer justify="space-between" alignItems="center">
                <GridItem>
                  <h3 className={classes.title}>
                    {t('promoted_ad.list.title')}
                  </h3>
                </GridItem>

                <GridItem>
                  <Button
                    color="primary"
                    simple
                    className={classes.showFilters}
                    onClick={() => refresh()}
                    id="refresh"
                    key="refresh"
                    style={{
                      marginRight: 24,
                    }}
                  >
                    <Refresh />
                    {t('common.refresh')}
                  </Button>
                  <WithPermission
                    allowPermissions={[
                      'add_promotedad',
                      'add_ownership_promotedad',
                    ]}
                  >
                    <WithPermission
                      allowPermissions={['list_promotedadpackage']}
                    >
                      <Button
                        data-cy="createPromotedAd"
                        color="success"
                        default
                        onClick={() => this.handleCreatePromotedAd()}
                        id="createPromotedAd"
                      >
                        {t('promoted_ad.create')}
                      </Button>
                    </WithPermission>
                  </WithPermission>
                </GridItem>
              </GridContainer>

              <GridContainer>
                <GridItem xs={12}>
                  <div className={classes.contentContainer}>
                    <div
                      className={[
                        classes.loadingLineContainer,
                        classes.topLine,
                      ].join(' ')}
                    >
                      {' '}
                      {fullLoading && (
                        <LinearProgress
                          classes={{
                            barColorPrimary: classes.loadingLine,
                          }}
                        />
                      )}
                    </div>
                    <GridContainer
                      justify="space-between"
                      alignItems="center"
                      className={[
                        classes.loteActionsAnimation,
                        selectedItems.length > 0 ? classes.hideFilters : '',
                      ].join(' ')}
                    >
                      <GridItem xs={12} sm={8} md={7}>
                        <div className={classes.searchFieldContainer}>
                          <Button
                            color="info"
                            round
                            simple={
                              !showFilters &&
                              JSON.stringify(appliedFilter) ===
                                JSON.stringify(defaultFilters)
                            }
                            // className={classes.filterBtn}
                            onClick={this.handleShowFilters}
                            id="filter"
                            key="filter"
                            data-cy="filterBtn"
                          >
                            <FilterIco />
                            <span>
                              {JSON.stringify(appliedFilter) ===
                              JSON.stringify(defaultFilters)
                                ? t('user.list.filter')
                                : t('user.list.filtering')}
                            </span>
                          </Button>
                        </div>
                        <Hidden
                          only={['sm', 'md', 'lg', 'xl']}
                          implementation="css"
                        >
                          <GridContainer>
                            <GridItem xs={12}>
                              <div
                                className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                              >
                                <Filter
                                  filter={filter}
                                  appliedFilter={appliedFilter}
                                  restartFilters={restartFilters}
                                  showFilters={this.handleShowFilters}
                                />
                              </div>
                            </GridItem>
                          </GridContainer>
                        </Hidden>
                      </GridItem>
                      <GridItem xs={12} sm={4} md={5}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.promotedAds.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                    <Hidden only={['xs']} implementation="css">
                      <GridContainer>
                        <GridItem xs={12}>
                          <div
                            className={`${classes.loteActionsAnimation}
                    ${
                      showFilters
                        ? classes.showPaidAdFilters
                        : classes.hideFilters
                    }`}
                          >
                            <Filter
                              filter={filter}
                              appliedFilter={appliedFilter}
                              restartFilters={restartFilters}
                              showFilters={this.handleShowFilters}
                            />
                          </div>
                        </GridItem>
                      </GridContainer>
                    </Hidden>
                    <div
                      className={[
                        classes.groupActions,
                        classes.loteActionsAnimation,
                        selectedItems.length > 0
                          ? classes.showLote
                          : classes.hideLote,
                      ].join(' ')}
                    >
                      <WithPermission allowPermissions={['publish_ad']}>
                        <Button
                          data-cy="publishLote"
                          color="success"
                          simple
                          onClick={() =>
                            this.handleAction('publish', selectedItems)
                          }
                          id="publishAd"
                        >
                          <Visibility />
                          {t('common.publish')}
                        </Button>
                      </WithPermission>
                      <WithPermission allowPermissions={['review_ad']}>
                        <Button
                          data-cy="reviewLote"
                          color="primary"
                          simple
                          onClick={() =>
                            this.handleAction('review', selectedItems)
                          }
                          id="reviewAd"
                        >
                          <Done />
                          {t('common.review')}
                        </Button>
                      </WithPermission>
                      <WithPermission
                        allowPermissions={[
                          'pause_resume_promotedad',
                          'pause_resume_ownership_promotedad',
                        ]}
                      >
                        <Button
                          data-cy="pauseLote"
                          color="primary"
                          simple
                          onClick={() =>
                            this.handleAction('pause', selectedItems)
                          }
                          id="pausePromotedAd"
                        >
                          <Pause />
                          {t('common.pause')}
                        </Button>
                        <Button
                          data-cy="resumeLote"
                          color="primary"
                          simple
                          onClick={() =>
                            this.handleAction('resume', selectedItems)
                          }
                          id="resumePromotedAd"
                        >
                          <Play />
                          {t('common.resume')}
                        </Button>
                      </WithPermission>
                      <WithPermission
                        allowPermissions={[
                          'extend_ownership_promotedad',
                          'extend_promotedad',
                        ]}
                      >
                        <Button
                          data-cy="extendLote"
                          color="primary"
                          simple
                          onClick={() =>
                            this.handleAction('extend', selectedItems)
                          }
                          id="extendPromotedAd"
                        >
                          <Schedule />
                          {t('common.extend')}
                        </Button>
                      </WithPermission>
                      <WithPermission
                        allowPermissions={[
                          'cancel_promotedad',
                          'cancel_ownership_promotedad',
                        ]}
                      >
                        <Button
                          data-cy="cancelLote"
                          color="danger"
                          simple
                          onClick={() =>
                            this.handleAction('cancel', selectedItems)
                          }
                          id="cancelPromotedAd"
                        >
                          <Close />
                          {t('common.cancel')}
                        </Button>
                      </WithPermission>
                    </div>
                    <GridContainer>
                      <GridItem xs={12}>
                        <div>
                          {loading ? (
                            <TableLoader />
                          ) : (
                            <ReactTable
                              data={rows}
                              filterable
                              columns={cols}
                              loading={loading}
                              pageSize={
                                edges.length < numberOfRowsSelected
                                  ? edges.length
                                  : numberOfRowsSelected
                              }
                              showPagination={false}
                              className={[
                                '-striped -highlight',
                                classes.tbl,
                              ].join(' ')}
                              expanded={loadingRows}
                              defaultFilterMethod={(field, row) => {
                                const id = field.pivotId || field.id;
                                return row[id] !== undefined
                                  ? String(row[id]).startsWith(field.value)
                                  : true;
                              }}
                              SubComponent={() => (
                                <div className={classes.loading}>
                                  <LoadingAnimation />
                                </div>
                              )}
                              getTrProps={(state, rowInfo) => ({
                                style: {
                                  backgroundColor:
                                    rowInfo &&
                                    (selectedItems.indexOf(
                                      rowInfo.original.id,
                                    ) !== -1
                                      ? 'rgba(171, 221, 250, .8)'
                                      : this.renderRowColor(
                                          rowInfo.original.id,
                                        )),
                                  borderLeft:
                                    rowInfo &&
                                    failedIds.indexOf(rowInfo.original.id) !==
                                      -1
                                      ? '4px solid red'
                                      : '',
                                },
                              })}
                              noDataText={t('promoted_ad.list.no_data')}
                              minRows={rows.length || 8}
                            />
                          )}
                        </div>
                      </GridItem>
                    </GridContainer>
                    <GridContainer justify="flex-end">
                      <GridItem xs={12} sm={4}>
                        {!fullLoading ? (
                          <Paginator
                            pageInfo={data.promotedAds.pageInfo}
                            changePage={changePage}
                            changeNumberOfRows={changeNumberOfRows}
                            paginationDirection={paginationDirection}
                            numberOfRowsSelected={numberOfRowsSelected}
                          />
                        ) : (
                          <ContentLoader height={67} width={327} speed={2}>
                            <path
                              // eslint-disable-next-line max-len
                              d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313708,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                              id="Combined-Shape"
                              fill="#D8D8D8"
                            />
                          </ContentLoader>
                        )}
                      </GridItem>
                    </GridContainer>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * PromotedAdList PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop ads query's results
 * @param itemProcessing - id of items that is processing
 * @param numberOfRowsSelected - prop current number of rows selected
 * @param appliedFilter - prop pass to Filter
 * @param loading - prop ads query or mutations status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with string translations
 * @param failedIds - prop from HOC with string translations
 * @param notification - prop to know if notification will be showed or not
 * @callback adReview - callback to review elements
 * @callback adPublish - callback to publish elements
 * @callback paidAdExtend - callback to extend paidad
 * @callback paidAdPause - callback to pause paidad
 * @callback paidAdResume - callback to resume paidad
 * @callback paidAdCancel - callback to cancel paidad
 * @callback changePage - prop pass to Paginator
 * @callback changeNumberOfRows - prop pass to Paginator
 * @callback filter - prop pass to Filter
 * @callback restartFilters - prop pass to Filter
 * @callback refresh - callback to refresh page
 * @callback closeNotification - callback to close notification
 */

PromotedAdList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  // eslint-disable-next-line react/require-default-props
  paginationDirection: PropTypes.string,
  adReview: PropTypes.func.isRequired,
  adPublish: PropTypes.func.isRequired,
  paidAdExtend: PropTypes.func.isRequired,
  paidAdPause: PropTypes.func.isRequired,
  paidAdResume: PropTypes.func.isRequired,
  paidAdCancel: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  refresh: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  notification: PropTypes.instanceOf(Object).isRequired,
  closeNotification: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PromotedAdWrapper = props => <PromotedAdList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(PromotedAdWrapper),
);
